import { sendVideoPageRelatedPlaylistClick, sendVideoPageRelatedVideoClick } from '@/analytics/ga';
import { DeviceStore } from '@/stores/device/device_detect';
import { ExperimentStore } from '@/stores/experiment/experiment_store';

type TrackerSource = 'old_video_page' | 'new_video_page';

const EXPERIMENT_KEY = 'video_page_redesign_ab_test';

export class ABTestClickTracker {
    private sourceVideoId: number;
    private experimentStore: ExperimentStore;
    private deviceStore: DeviceStore;
    private variationId: number | null = null;
    private trackerSource: TrackerSource = 'new_video_page';

    constructor(sourceVideoId: number) {
        this.sourceVideoId = sourceVideoId;
        this.experimentStore = new ExperimentStore();
        this.deviceStore = new DeviceStore();
    }

    init(trackerSource: TrackerSource = 'new_video_page'): void {
        const variation = this.experimentStore.getExperimentVariation(EXPERIMENT_KEY);
        if (variation === null) {
            return;
        }

        this.variationId = variation;
        this.trackerSource = trackerSource;

        this.setupVideoClickTracking();
        this.setupPlaylistClickTracking();
    }

    private setupVideoClickTracking(): void {
        const sidebarRef = document.querySelector<HTMLElement>('.js-related-videos-right');
        const belowVideoRef = document.querySelector<HTMLElement>('.js-related-videos-bottom');

        if (sidebarRef) {
            this.attachVideoClickListeners(sidebarRef, 'sidebar');
        }

        if (belowVideoRef) {
            this.attachVideoClickListeners(belowVideoRef, 'below_video');
        }
    }

    private attachVideoClickListeners(container: HTMLElement, list: 'sidebar' | 'below_video'): void {
        const videoItems = container.querySelectorAll<HTMLElement>('.js-video-item, .video-item');

        videoItems.forEach((item, index) => {
            item.addEventListener('click', () => {
                const targetId = parseInt(item.getAttribute('data-id') || '0', 10);
                if (targetId > 0 && this.variationId !== null) {
                    sendVideoPageRelatedVideoClick({
                        sourceId: this.sourceVideoId,
                        targetId: targetId,
                        list: list,
                        position: index + 1,
                        variationId: this.variationId,
                    });
                }
            });
        });
    }

    private setupPlaylistClickTracking(): void {
        const playlistContainer = document.querySelector<HTMLElement>('.js-related-playlists');

        if (!playlistContainer) {
            return;
        }

        const playlistItems = playlistContainer.querySelectorAll<HTMLElement>('.js-playlist-item');

        if (this.deviceStore.isMobile()) {
            this.attachPlaylistClickListenersMobile(playlistItems);
        } else {
            if (this.trackerSource === 'new_video_page') {
                this.attachPlaylistClickListenersDesktop(playlistItems);
            } else {
                this.attachPlaylistClickListenersLegacyDesktop(playlistItems);
            }
        }
    }

    private attachPlaylistClickListenersMobile(playlistItems: NodeListOf<HTMLElement>): void {
        playlistItems.forEach((item, index) => {
            item.addEventListener('click', () => {
                this.sendPlaylistClickEvent(item, index);
            });
        });
    }

    private attachPlaylistClickListenersDesktop(playlistItems: NodeListOf<HTMLElement>): void {
        playlistItems.forEach((item, index) => {
            const actionButtons = item.querySelectorAll<HTMLAnchorElement>('.js-playlist-action');
            actionButtons.forEach((button) => {
                button.addEventListener('click', () => {
                    this.sendPlaylistClickEvent(item, index);
                });
            });
        });
    }

    private attachPlaylistClickListenersLegacyDesktop(playlistItems: NodeListOf<HTMLElement>): void {
        playlistItems.forEach((item, index) => {
            item.addEventListener('click', () => {
                this.sendPlaylistClickEvent(item, index);
            });
        });
    }

    private sendPlaylistClickEvent(item: HTMLElement, index: number): void {
        const targetId = parseInt(item.getAttribute('data-id') || '0', 10);
        if (targetId > 0 && this.variationId !== null) {
            sendVideoPageRelatedPlaylistClick({
                sourceId: this.sourceVideoId,
                targetId: targetId,
                position: index + 1,
                variationId: this.variationId,
            });
        }
    }
}
